import { Loading } from "../../app/Status";
import { Fragment, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  type ContractCallDto,
  ALLOCATION_LABELS,
  CONTRACT_BILLING_LABELS,
  CONTRACT_LINE_KIND_LABELS,
  CONTRACTED_BY_LABELS,
  INDEX_METHOD_LABELS,
  PRICE_FORM_LABELS,
  PRICE_REASON_LABELS,
  TASK_STATUS_LABELS,
  type ContractDetailDto,
  type TaskStatus,
  type ContractLineDto,
} from "@klantpunt/shared";
import { Tabs } from "../../app/Choice";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { buildingPath } from "../../app/navItems";
import { PageHeader } from "../../app/PageHeader";
import { api } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { euroInput } from "../mjop/MjopFields";
import { parseEuro } from "../../pages/Platform";
import { ContractDialog } from "./ContractDialog";
import { termText } from "./ContractsPage";
import { AssetCoverDialog, CoverDialog, LineDialog, PriceDialog } from "./LineDialogs";
import {
  buyoutParts,
  errMsg,
  euro,
  fmtDate,
  intervalText,
  pct,
  priceText,
  stateTone,
  useContract,
  useContractCalls,
  useContractsRefresh,
} from "./useContracts";
import { PriceChart } from "./PriceChart";

type Tab = "afspraken" | "posten" | "dekking" | "storingen";

/** Eén contract (docs/CONTRACTEN.md): afspraken, posten met prijzen en de gedekte uitvoeringen. */
export function ContractPage() {
  const { contractId: id = "" } = useParams();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useContractsRefresh();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: Tab = raw === "posten" || raw === "dekking" || raw === "storingen" ? raw : "afspraken";
  const q = useContract(id);
  const canEdit = can(me, "contract.manage");
  const [editing, setEditing] = useState(false);
  const [lineDialog, setLineDialog] = useState<ContractLineDto | "new" | null>(null);
  const [priceFor, setPriceFor] = useState<ContractLineDto | null>(null);
  const [coverFor, setCoverFor] = useState<ContractLineDto | "any" | null>(null);
  const [assetsFor, setAssetsFor] = useState<ContractLineDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (!hasModule(me, "contracts"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Contracten.</p>
      </div>
    );
  if (q.isPending) return <Loading page />;
  if (q.isError || !q.data)
    return (
      <div className="center">
        <p>{q.isError ? errMsg(q.error) : "Contract niet gevonden."}</p>
        <Link to="/contracten">Naar de contracten</Link>
      </div>
    );
  const c = q.data;
  const setTab = (v: Tab) => {
    const p = new URLSearchParams(params);
    if (v === "afspraken") p.delete("tab");
    else p.set("tab", v);
    setParams(p, { replace: true });
  };
  const covered = c.lines.reduce((n, l) => n + l.obligations.length + l.assets.length, 0);
  const breakdowns = c.lines.filter((l) => l.kind === "breakdown");
  const calls = breakdowns.reduce((n, l) => n + l.callsThisYear, 0);

  const action = !canEdit ? undefined : tab === "afspraken" ? (
    <button type="button" className="btn primary" onClick={() => setEditing(true)}>
      Afspraken bewerken
    </button>
  ) : tab === "posten" ? (
    <button type="button" className="btn primary" onClick={() => setLineDialog("new")}>
      + Post
    </button>
  ) : tab === "storingen" ? undefined : c.lines.length ? (
    <button type="button" className="btn primary" onClick={() => setCoverFor("any")}>
      + Uitvoeringen
    </button>
  ) : undefined;

  return (
    <div className="page dir-page contract-page">
      <PageHeader
        label={<Link to="/contracten">Contracten</Link>}
        title={c.name}
        action={action}
        menu={
          canEdit ? (
            <button type="button" className="menuitem danger" onClick={() => setDeleting(true)}>
              Contract verwijderen
            </button>
          ) : undefined
        }
        tabs={
          <Tabs
            label="Contract"
            value={tab}
            onChange={setTab}
            items={[
              { value: "afspraken", label: "Afspraken" },
              { value: "posten", label: "Posten", count: c.lines.length },
              { value: "dekking", label: "Dekking", count: covered },
              ...(breakdowns.length ? [{ value: "storingen" as const, label: "Storingen", count: calls }] : []),
            ]}
          />
        }
      />
      <p className="contract-sub">
        <span className={`chip tone ${stateTone(c)}`.trim()}>{termText(c)}</span>{" "}
        <Meta
          className="small muted"
          items={[c.supplier?.name, c.client.name, c.yearCents != null && `${euro(c.yearCents)} per jaar`]}
        />
      </p>
      {c.amountsHidden && (
        <p className="hint">Bedragen en prijzen van dit contract zijn niet zichtbaar in een klantaccount.</p>
      )}
      {tab === "afspraken" && <Agreements c={c} />}
      {tab === "posten" && (
        <Lines
          c={c}
          canEdit={canEdit}
          onEdit={(l) => setLineDialog(l)}
          onPrice={(l) => setPriceFor(l)}
          onDelete={async (l) => {
            try {
              await refresh(await api<ContractDetailDto>(`/contract-lines/${l.id}`, { method: "DELETE" }));
              toast("Post verwijderd");
            } catch (e) {
              toast(errMsg(e));
            }
          }}
        />
      )}
      {tab === "dekking" && (
        <Coverage c={c} canEdit={canEdit} onAdd={(l) => setCoverFor(l)} onAddAssets={(l) => setAssetsFor(l)} />
      )}
      {tab === "storingen" && <Calls c={c} />}
      {assetsFor && <AssetCoverDialog contract={c} line={assetsFor} onClose={() => setAssetsFor(null)} />}
      {editing && <ContractDialog contract={c} onClose={() => setEditing(false)} />}
      {lineDialog && (
        <LineDialog
          contract={c}
          line={lineDialog === "new" ? undefined : lineDialog}
          onClose={() => setLineDialog(null)}
        />
      )}
      {priceFor && <PriceDialog line={priceFor} onClose={() => setPriceFor(null)} />}
      {coverFor && (
        <CoverDialog contract={c} line={coverFor === "any" ? undefined : coverFor} onClose={() => setCoverFor(null)} />
      )}
      {deleting && (
        <Modal
          title="Contract verwijderen"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setDeleting(false)}>
                Annuleren
              </button>
              <button
                type="button"
                className="btn primary danger"
                onClick={async () => {
                  try {
                    await api(`/contracts/${c.id}`, { method: "DELETE" });
                    await refresh();
                    toast("Contract verwijderd");
                    navigate("/contracten");
                  } catch (e) {
                    toast(errMsg(e));
                  }
                }}
              >
                Verwijderen
              </button>
            </>
          }
        >
          <p>
            {c.name} met de posten en prijzen verdwijnt. De uitvoeringen blijven staan, zonder contract. Is het contract
            afgelopen, zet het dan liever op Opgezegd.
          </p>
        </Modal>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  if (v === null || v === undefined || v === "") return null;
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

function Agreements({ c }: { c: ContractDetailDto }) {
  return (
    <div className="contract-grid">
      <section className="panel">
        <h2 className="label">Afspraken</h2>
        <dl className="kv">
          <Row
            k="Leverancier"
            v={
              c.supplier && (
                <>
                  {c.supplier.name}
                  {c.person && <div className="small muted">{c.person.name}</div>}
                  {c.supplier.emergencyPhone && (
                    <div className="small">
                      Storingsnummer <a href={`tel:${c.supplier.emergencyPhone}`}>{c.supplier.emergencyPhone}</a>
                    </div>
                  )}
                </>
              )
            }
          />
          <Row k="Contractnummer" v={c.reference} />
          <Row k="Klant" v={c.client.name} />
          <Row k="Afgesloten door" v={CONTRACTED_BY_LABELS[c.contractedBy]} />
          <Row k="Ingangsdatum" v={fmtDate(c.startsOn)} />
          <Row
            k="Einddatum"
            v={
              c.endsOn
                ? `${fmtDate(c.endsOn)}${c.currentEndOn && c.currentEndOn !== c.endsOn ? `, verlengd tot ${fmtDate(c.currentEndOn)}` : ""}`
                : "Onbepaalde tijd"
            }
          />
          <Row
            k="Verlengen"
            v={c.autoRenew ? `Stilzwijgend, telkens ${c.renewMonths ?? 12} maanden` : "Niet stilzwijgend"}
          />
          <Row
            k="Opzeggen"
            v={
              c.noticeMonths != null &&
              `${c.noticeMonths} ${c.noticeMonths === 1 ? "maand" : "maanden"} opzegtermijn${c.noticeBy ? `, uiterlijk ${fmtDate(c.noticeBy)}` : ""}`
            }
          />
          <Row
            k="Indexatie"
            v={
              c.indexMethod === "fixed" && c.indexPercent != null
                ? `${String(c.indexPercent).replace(".", ",")}% per jaar`
                : INDEX_METHOD_LABELS[c.indexMethod]
            }
          />
          <Row k="Responstijden" v={c.responseTimes} />
          <Row
            k="Document"
            v={
              c.document && (
                <Link to={`${buildingPath(c.document.buildingId, "dossier")}?document=${c.document.id}`}>
                  {c.document.title}
                </Link>
              )
            }
          />
        </dl>
        {c.summary && <p className="contract-summary">{c.summary}</p>}
      </section>
      <section className="panel">
        <h2 className="label">Gebouwen</h2>
        <ul className="contract-list">
          {c.buildings.map((b) => (
            <li key={b.id}>
              <Link to={buildingPath(b.id, "contracten")}>
                <span className="code">{b.code}</span> {b.name}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Lines({
  c,
  canEdit,
  onEdit,
  onPrice,
  onDelete,
}: {
  c: ContractDetailDto;
  canEdit: boolean;
  onEdit: (l: ContractLineDto) => void;
  onPrice: (l: ContractLineDto) => void;
  onDelete: (l: ContractLineDto) => Promise<void>;
}) {
  if (!c.lines.length)
    return (
      <p className="muted">
        Nog geen posten. Een post is één afspraak, bijvoorbeeld Preventief onderhoud 4x per jaar of Storingen liften.
      </p>
    );
  return (
    <div className="contract-lines">
      {c.lines.map((l) => (
        <section key={l.id} className="panel" aria-label={l.description}>
          <div className="contract-linehead">
            <div className="grow">
              <h2 className="contract-linetitle">{l.description}</h2>
              <Meta
                className="small muted"
                items={[
                  CONTRACT_LINE_KIND_LABELS[l.kind],
                  CONTRACT_BILLING_LABELS[l.billing],
                  l.priceForm && PRICE_FORM_LABELS[l.priceForm],
                  l.building ? `${l.building.code} ${l.building.name}` : c.buildings.length > 1 && "Alle gebouwen",
                  l.obligations.length === 1 ? "1 uitvoering" : `${l.obligations.length} uitvoeringen`,
                  l.kind === "breakdown" && (l.assets.length === 1 ? "1 asset" : `${l.assets.length} assets`),
                ]}
              />
              {l.kind === "breakdown" && <Meta className="small" items={buyoutParts(l)} />}
            </div>
            {canEdit && (
              <span className="acts">
                <button type="button" className="btn small" onClick={() => onPrice(l)}>
                  Nieuwe prijs
                </button>
                <button type="button" className="btn small ghost" onClick={() => onEdit(l)}>
                  Bewerken
                </button>
                <button type="button" className="btn small ghost" onClick={() => void onDelete(l)}>
                  Verwijderen
                </button>
              </span>
            )}
          </div>
          {!c.amountsHidden && (
            <p className="contract-price">
              <strong>{priceText(l, l.price)}</strong>
              {l.yearCents != null && (l.billing === "cost_plus" || l.priceForm === "per_visit") && (
                <span className="small muted">
                  {" "}
                  {l.billing === "cost_plus" ? "raming" : "samen"} {euro(l.yearCents)} per jaar
                </span>
              )}
            </p>
          )}
          {l.notes && <p className="small">{l.notes}</p>}
          <PriceChart line={l} indexPercent={c.effectiveIndexPercent} />
          {l.prices.length > 0 && (
            <div className="tscroll">
              <table className="it" aria-label={`Prijzen ${l.description}`}>
                <thead>
                  <tr>
                    <th scope="col">Geldig vanaf</th>
                    <th scope="col">Prijs</th>
                    <th scope="col" className="num">
                      Wijziging
                    </th>
                    <th scope="col" className="dir-hide-narrow">
                      Reden
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {l.prices.map((p) => (
                    <tr key={p.id} aria-current={p.id === l.price?.id ? "true" : undefined}>
                      <td>
                        {fmtDate(p.validFrom)}
                        {p.id === l.price?.id && <div className="small muted">Geldt nu</div>}
                      </td>
                      <td>
                        {priceText(l, p)}
                        {p.note && <div className="small muted">{p.note}</div>}
                      </td>
                      <td className="num">{pct(p.changePercent) || "–"}</td>
                      <td className="dir-hide-narrow">
                        {PRICE_REASON_LABELS[p.reason]}
                        {p.createdBy && <div className="small muted">{p.createdBy}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function Coverage({
  c,
  canEdit,
  onAdd,
  onAddAssets,
}: {
  c: ContractDetailDto;
  canEdit: boolean;
  onAdd: (l: ContractLineDto) => void;
  onAddAssets: (l: ContractLineDto) => void;
}) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const [shares, setShares] = useState<Record<string, string>>({});
  if (!c.lines.length) return <p className="muted">Maak eerst een post; daarna koppel je de uitvoeringen.</p>;

  async function remove(l: ContractLineDto, id: string) {
    try {
      await refresh(
        await api<ContractDetailDto>(`/contract-lines/${l.id}/obligations`, { method: "POST", body: { remove: [id] } }),
      );
      toast("Uitvoering uit het contract gehaald");
    } catch (e) {
      toast(errMsg(e));
    }
  }
  async function removeAsset(l: ContractLineDto, id: string) {
    try {
      await refresh(
        await api<ContractDetailDto>(`/contract-lines/${l.id}/assets`, { method: "POST", body: { remove: [id] } }),
      );
      toast("Asset uit de afkoop gehaald");
    } catch (e) {
      toast(errMsg(e));
    }
  }
  async function saveShare(id: string, v: string) {
    const cents = v.trim() ? parseEuro(v) : null;
    if (v.trim() && cents === null) return toast("Vul een bedrag in, bijv. 300,00");
    try {
      await refresh(
        await api<ContractDetailDto>(`/asset-obligations/${id}/contract-share`, {
          method: "PUT",
          body: { shareCents: cents },
        }),
      );
    } catch (e) {
      toast(errMsg(e));
    }
  }

  return (
    <div className="contract-lines">
      {c.lines.map((l) => {
        const period = l.billing === "fixed" && l.priceForm === "period";
        const manual = period && l.allocation === "manual";
        const allocated = period && l.kind !== "breakdown";
        return (
          <section key={l.id} className="panel" aria-label={`Dekking ${l.description}`}>
            <div className="contract-linehead">
              <div className="grow">
                <h2 className="contract-linetitle">{l.description}</h2>
                <Meta
                  className="small muted"
                  items={[
                    !c.amountsHidden && priceText(l, l.price),
                    !c.amountsHidden &&
                      allocated &&
                      `prijs per keer ${ALLOCATION_LABELS[l.allocation].toLocaleLowerCase("nl")}`,
                    ...(l.kind === "breakdown" ? buyoutParts(l) : []),
                  ]}
                />
              </div>
              {canEdit && (
                <span className="acts">
                  {l.kind === "breakdown" && (
                    <button type="button" className="btn small" onClick={() => onAddAssets(l)}>
                      + Assets
                    </button>
                  )}
                  <button
                    type="button"
                    className={l.kind === "breakdown" ? "btn small ghost" : "btn small"}
                    onClick={() => onAdd(l)}
                  >
                    + Uitvoeringen
                  </button>
                </span>
              )}
            </div>
            {l.kind === "breakdown" && (
              <AssetTable l={l} canEdit={canEdit} onRemove={(id) => void removeAsset(l, id)} />
            )}
            {l.kind === "breakdown" && !l.obligations.length ? null : !l.obligations.length ? (
              <p className="muted small">Nog geen uitvoeringen onder deze post.</p>
            ) : (
              <div className="tscroll">
                <table className="it" aria-label={`Uitvoeringen ${l.description}`}>
                  <thead>
                    <tr>
                      <th scope="col">Uitvoering</th>
                      <th scope="col" className="dir-hide-narrow">
                        Gebouw
                      </th>
                      <th scope="col" className="num">
                        {period ? "Per keer (indicatie)" : "Per keer"}
                      </th>
                      {canEdit && (
                        <th scope="col" className="sr-only">
                          Acties
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {l.obligations.map((o) => (
                      <tr key={o.id}>
                        <td className="tcell-title">
                          <Link
                            to={
                              o.asset
                                ? `${buildingPath(o.building.id, "assets")}?asset=${o.asset.id}`
                                : buildingPath(o.building.id, "keuringen")
                            }
                          >
                            {o.name}
                          </Link>
                          <div className="small muted">
                            <Meta
                              items={[
                                o.asset ? `${o.asset.code} ${o.asset.name}`.trim() : "Bij het gebouw",
                                intervalText(o),
                                o.nextDueOn && `volgende ${fmtDate(o.nextDueOn)}`,
                              ]}
                            />
                          </div>
                        </td>
                        <td className="dir-hide-narrow">
                          <span className="code">{o.building.code}</span> {o.building.name}
                        </td>
                        <td className="num">
                          {manual && canEdit ? (
                            <input
                              className="contract-share"
                              inputMode="decimal"
                              aria-label={`Aandeel per keer ${o.name}`}
                              value={shares[o.id] ?? euroInput(o.manualShareCents)}
                              onChange={(e) => setShares((s) => ({ ...s, [o.id]: e.target.value }))}
                              onBlur={(e) => void saveShare(o.id, e.target.value)}
                            />
                          ) : period ? (
                            euro(o.shareCents) || "–"
                          ) : (
                            euro(o.costCents) || "–"
                          )}
                        </td>
                        {canEdit && (
                          <td className="num">
                            <button
                              type="button"
                              className="btn small ghost"
                              onClick={() => void remove(l, o.id)}
                              aria-label={`${o.name} uit het contract halen`}
                            >
                              Eruit halen
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Assets waarvan de storingen onder de post vallen (afkoop). */
function AssetTable({
  l,
  canEdit,
  onRemove,
}: {
  l: ContractLineDto;
  canEdit: boolean;
  onRemove: (id: string) => void;
}) {
  if (!l.assets.length)
    return (
      <p className="muted small">
        Nog geen assets. Kies + Assets om de storingen van assets onder deze post te brengen.
      </p>
    );
  return (
    <div className="tscroll">
      <table className="it" aria-label={`Assets ${l.description}`}>
        <thead>
          <tr>
            <th scope="col">Asset</th>
            <th scope="col" className="dir-hide-narrow">
              Gebouw
            </th>
            {canEdit && (
              <th scope="col" className="sr-only">
                Acties
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {l.assets.map((a) => (
            <tr key={a.id}>
              <td className="tcell-title">
                <Link to={`${buildingPath(a.building.id, "assets")}?asset=${a.id}`}>
                  <span className="code">{a.code}</span> {a.name}
                </Link>
              </td>
              <td className="dir-hide-narrow">
                <span className="code">{a.building.code}</span> {a.building.name}
              </td>
              {canEdit && (
                <td className="num">
                  <button
                    type="button"
                    className="btn small ghost"
                    onClick={() => onRemove(a.id)}
                    aria-label={`${a.name} uit de afkoop halen`}
                  >
                    Eruit halen
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Duur in uren en minuten: 45 min, 3 u, 2 u 15 min, 2 d 4 u. */
export function fmtDuration(min: number) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h >= 48) return `${Math.floor(h / 24)} d${h % 24 ? ` ${h % 24} u` : ""}`;
  return `${h} u${min % 60 ? ` ${min % 60} min` : ""}`;
}

/** Reactietijd van een storing: van melden tot opgepakt, tegen de reactietijd van de post. */
function ResponseCell({ r, hours }: { r: ContractCallDto; hours: number | null }) {
  const open = !r.startedAt && r.status === "open";
  return (
    <>
      <span className={open ? "muted" : undefined}>
        {open ? `${fmtDuration(r.responseMinutes)} open` : r.startedAt ? fmtDuration(r.responseMinutes) : "—"}
      </span>
      {r.onTime === false && (
        <span className="block">
          <span className="chip tone tone-warn">Te laat</span>
        </span>
      )}
      {r.onTime === true && hours != null && <span className="sr-only">, binnen {hours} uur</span>}
    </>
  );
}

/** Storingen per jaar onder de storingsposten, met de teller tegen het maximum en de reactietijd. */
function Calls({ c }: { c: ContractDetailDto }) {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const q = useContractCalls(c.id, year);
  const lines = c.lines.filter((l) => l.kind === "breakdown");
  const rows = q.data ?? [];
  return (
    <>
      <div className="contract-sub">
        <label className="field inline">
          <span>Jaar</span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[now, now - 1, now - 2].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        {lines.map((l) => {
          const own = rows.filter((r) => r.lineId === l.id);
          const n = own.length;
          const judged = own.filter((r) => r.onTime !== null);
          const late = judged.filter((r) => r.onTime === false).length;
          const over = l.maxCallsPerYear != null && n > l.maxCallsPerYear;
          return (
            <Fragment key={l.id}>
              <span className={over ? "chip tone tone-warn" : "chip"}>
                {l.description}: {n}
                {l.billing === "fixed" && l.maxCallsPerYear != null ? ` van ${l.maxCallsPerYear}` : ""}
              </span>
              {l.responseHours != null && judged.length > 0 && (
                <span className={late ? "chip tone tone-warn" : "chip tone tone-ok"}>
                  Op tijd: {judged.length - late} van {judged.length}
                </span>
              )}
            </Fragment>
          );
        })}
      </div>
      {q.isPending ? (
        <Loading />
      ) : !rows.length ? (
        <p className="muted">
          Geen storingen in {year}. Een storing komt hier als je een taak aan een storingspost van dit contract koppelt.
        </p>
      ) : (
        <div className="tscroll">
          <table className="it" aria-label="Storingen">
            <thead>
              <tr>
                <th scope="col">Storing</th>
                <th scope="col" className="dir-hide-narrow">
                  Gebouw
                </th>
                <th scope="col">Gemeld</th>
                <th scope="col">Reactie</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const l = lines.find((x) => x.id === r.lineId);
                return (
                  <tr key={r.id}>
                    <td className="tcell-title">
                      <Link to={`${buildingPath(r.building.id, "taken")}?taak=${r.id}`}>
                        <span className="code">{r.code}</span> {r.title}
                      </Link>
                      <div className="small muted">
                        <Meta items={[lines.length > 1 && l?.description, `nr. ${r.nr} in ${year}`]} />
                      </div>
                      {r.overMax && <span className="chip tone tone-warn">Boven het maximum</span>}
                    </td>
                    <td className="dir-hide-narrow">
                      <span className="code">{r.building.code}</span> {r.building.name}
                    </td>
                    <td>{fmtDate(r.createdAt.slice(0, 10))}</td>
                    <td>
                      <ResponseCell r={r} hours={l?.responseHours ?? null} />
                    </td>
                    <td>{TASK_STATUS_LABELS[r.status as TaskStatus] ?? r.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
